import localStorageUtil from '$/common/utils/localStorageUtil';
import { resolveAgentTeamDownloadUrl } from '$/domains/teamAgent/teamAgentService';
import type { AgentTeamDownloadUrlResolveParams, AigcWaterMarkSwitch } from '$/domains/teamAgent/type';

export type AigcWaterMarkState = '0' | '1';

/**
 * 判断当前媒体类型是否需要接入“无品牌水印直出”会员引导。
 */
export function isNoBrandWatermarkGuideMedia(mediaType?: string): mediaType is 'image' | 'video' {
  return mediaType === 'image' || mediaType === 'video';
}

/**
 * 将设置面板里的 AIGC 水印开关转换为后端约定值；未设置时按默认开启处理。
 */
export function getAigcWaterMarkSwitch(): AigcWaterMarkSwitch {
  if (typeof window === 'undefined') return 1;
  return localStorageUtil.getItem('RoboneoDownloadHasWatermark') === '0' ? 2 : 1;
}

/**
 * 读取当前 AIGC 水印状态；未设置时保持产品默认开启。
 */
export function getAigcWaterMarkState(): AigcWaterMarkState {
  return getAigcWaterMarkSwitch() === 1 ? '1' : '0';
}

/**
 * 将旧水印弹窗状态转换为后端下载解析接口的 AIGC 开关。
 */
export function getAigcWaterMarkSwitchByState(state: AigcWaterMarkState): AigcWaterMarkSwitch {
  return state === '1' ? 1 : 2;
}

/**
 * 解析会员无品牌水印下载地址（本地降级：无接口时回落原图）。
 */
export function resolveNoBrandWatermarkDownloadUrl({
  url,
}: {
  url: string;
  watermarkUrl?: string;
}): string {
  return url;
}

/**
 * 下载前请求后端解析最终 URL。
 * `/api/file_system/download_url_resolve` 在关闭 AIGC（switch=2）时可能直出无品牌水印原图，
 * 此时回落 `media_url_by_water_mark`，保证国内下载仍带品牌水印；开启 AIGC 时使用接口返回。
 * 旧入口保留降级；明确要求权益解析的入口失败时交由 UI 反馈。
 */
export async function resolveNoBrandWatermarkDownloadUrlByApi({
  url,
  watermarkUrl,
  aigcWaterMarkSwitch,
  fallbackUrl,
  fallbackOnError = true,
  preferApiUrl = false,
}: {
  url?: string;
  watermarkUrl?: string;
  aigcWaterMarkSwitch?: AigcWaterMarkSwitch;
  fallbackUrl?: string;
  fallbackOnError?: boolean;
  /** 使用接口返回地址，即使 AIGC 水印关闭时也不回退到本地品牌水印图。 */
  preferApiUrl?: boolean;
}): Promise<string> {
  const resolvedFallbackUrl = fallbackUrl || url || watermarkUrl || '';
  if (!resolvedFallbackUrl) return resolvedFallbackUrl;

  const switchValue = aigcWaterMarkSwitch ?? getAigcWaterMarkSwitch();
  const brandWatermarkUrl = watermarkUrl?.trim() || '';

  try {
    const requestParams: AgentTeamDownloadUrlResolveParams = {
      media_url_by_water_mark: brandWatermarkUrl || undefined,
      aigc_watermark_switch: switchValue,
    };
    if (url) requestParams.media_url = url;
    const result = await resolveAgentTeamDownloadUrl(requestParams);
    const resolved = result.download_url || resolvedFallbackUrl;
    // 关闭 AIGC：接口可能丢掉品牌水印，强制回落到品牌水印地址。
    if (!preferApiUrl && switchValue === 2 && brandWatermarkUrl) {
      return brandWatermarkUrl;
    }
    return resolved;
  } catch (error) {
    if (!fallbackOnError) throw error;
    return resolvedFallbackUrl;
  }
}
